import { Fragment, useEffect, useState } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';

const RAG = {
  red:   { dot: '#EF4444', bar: 'bg-red-400',     text: 'text-red-700' },
  amber: { dot: '#F59E0B', bar: 'bg-amber-400',   text: 'text-amber-700' },
  green: { dot: '#10B981', bar: 'bg-emerald-400', text: 'text-emerald-700' },
  none:  { dot: '#D1D5DB', bar: 'bg-gray-200',    text: 'text-gray-400' },
};

function Movement({ m }) {
  if (m === 'improving') return <span className="text-emerald-600 font-bold" title="Fewer red/amber than 4 weeks ago">▼ improving</span>;
  if (m === 'worsening') return <span className="text-red-600 font-bold" title="More red/amber than 4 weeks ago">▲ worsening</span>;
  return <span className="text-gray-400" title="No change vs 4 weeks ago">– flat</span>;
}

export default function PmoPortfolioPage() {
  const { navigate } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('pmo-portfolio').then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading portfolio…</div>;
  if (!data) return <div className="p-6 text-red-400 text-sm">Failed to load portfolio.</div>;

  const { trend, owners, totals, weeks } = data;
  const maxTotal = Math.max(1, ...trend.map(w => w.red + w.amber + w.green + w.none));

  return (
    <div className="fade-in">
      <div className="mb-3">
        <h2 className="text-base font-extrabold text-navy">Portfolio Command Centre</h2>
        <div className="text-[9px] text-gray-500 mt-0.5">Delivery health trend &amp; per-owner rollup · {totals.projects} active projects · as at {formatDate(data.generated_at)}</div>
      </div>

      {/* Current exposure tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {[
          { label: 'Red', val: totals.red, cls: 'text-red-700 bg-red-50 border-red-200' },
          { label: 'Amber', val: totals.amber, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
          { label: 'Green', val: totals.green, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
          { label: 'Overdue projects', val: totals.overdue, cls: 'text-rose-700 bg-rose-50 border-rose-200' },
        ].map(t => (
          <div key={t.label} className={`px-3 py-2 rounded-lg border ${t.cls}`}>
            <div className="text-lg font-extrabold leading-none">{t.val}</div>
            <div className="text-[8px] font-bold mt-0.5">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Trend — 12-week RAG composition */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-bold text-navy uppercase">Portfolio RAG — last 12 weeks</h3>
          <div className="flex items-center gap-2 text-[8px]">
            {['red', 'amber', 'green', 'none'].map(k => (
              <span key={k} className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: RAG[k].dot }} />{k === 'none' ? 'No update' : k}</span>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-1 h-32">
          {trend.map((w, i) => {
            const total = w.red + w.amber + w.green + w.none || 1;
            const scale = total / maxTotal;  // weeks should be equal, but stay robust
            return (
              <div key={w.week} className="flex-1 flex flex-col justify-end" title={`Week of ${formatDate(w.week)} — red ${w.red}, amber ${w.amber}, green ${w.green}, no update ${w.none}`}>
                <div className="flex flex-col rounded-t overflow-hidden" style={{ height: `${scale * 100}%` }}>
                  {['red', 'amber', 'green', 'none'].map(k => w[k] > 0 && (
                    <div key={k} className={RAG[k].bar} style={{ height: `${(w[k] / total) * 100}%` }} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-1 mt-1">
          {trend.map((w, i) => (
            <div key={w.week} className="flex-1 text-center text-[6px] text-gray-400">{(i % 2 === 0 || i === trend.length - 1) ? formatDate(w.week).replace(/ \d{4}$/, '') : ''}</div>
          ))}
        </div>
      </div>

      {/* Per-owner rollup */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b bg-gray-50 text-[10px] font-bold text-navy uppercase">Delivery owners</div>
        <div className="overflow-x-auto"><table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-gray-50/60 text-[8px] uppercase text-gray-500">
              <th className="px-3 py-1.5 text-left">Owner</th>
              <th className="px-2 py-1.5 text-center">Projects</th>
              <th className="px-2 py-1.5 text-center">R / A / G</th>
              <th className="px-2 py-1.5 text-center">Overdue</th>
              <th className="px-2 py-1.5 text-center">Stale</th>
              <th className="px-2 py-1.5 text-center">Max slip</th>
              <th className="px-3 py-1.5 text-left">4-wk trend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {owners.map(o => {
              const key = o.owner_id || o.owner_name;
              const open = expanded === key;
              return (
                <Fragment key={key}>
                  <tr className="hover:bg-gray-50/60 cursor-pointer" onClick={() => setExpanded(open ? null : key)}>
                    <td className="px-3 py-2 font-semibold text-navy">
                      <span className="text-gray-400 mr-1">{open ? '▾' : '▸'}</span>{o.owner_name}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-700">{o.projects}</td>
                    <td className="px-2 py-2 text-center">
                      <span className="inline-flex items-center gap-1">
                        <b className={RAG.red.text}>{o.red}</b><span className="text-gray-300">/</span>
                        <b className={RAG.amber.text}>{o.amber}</b><span className="text-gray-300">/</span>
                        <b className={RAG.green.text}>{o.green}</b>
                        {o.none > 0 && <span className="text-gray-400 ml-1">({o.none} no RAG)</span>}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">{o.overdue > 0 ? <span className="font-bold text-rose-600">{o.overdue}</span> : <span className="text-gray-300">0</span>}</td>
                    <td className="px-2 py-2 text-center">{o.stale > 0 ? <span className="font-bold text-amber-600">{o.stale}</span> : <span className="text-gray-300">0</span>}</td>
                    <td className="px-2 py-2 text-center">{o.maxSlip > 0 ? <span className="text-orange-600 font-semibold">{o.maxSlip}d</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2 text-[9px]"><Movement m={o.movement} /></td>
                  </tr>
                  {open && (
                    <tr className="bg-gray-50/40">
                      <td colSpan={7} className="px-3 py-2">
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1">
                          {o.items.map(it => (
                            <button
                              key={it.sow_id}
                              onClick={(e) => { e.stopPropagation(); navigate('delivery-cockpit'); }}
                              className="flex items-center gap-1.5 text-left px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-[9px]"
                              title="Open in Delivery Cockpit"
                            >
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: it.worst ? RAG[it.worst].dot : RAG.none.dot }} />
                              <span className="font-semibold text-navy">{it.reference}</span>
                              {it.overdue > 0 && <span className="text-[7px] font-bold text-rose-600 uppercase ml-auto">overdue</span>}
                              {!it.overdue && it.maxSlip > 0 && <span className="text-[7px] text-orange-600 ml-auto">+{it.maxSlip}d</span>}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {owners.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No active projects</td></tr>}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
